// AddRequestPanel — RRA v2.2 (WEL-180): "Add a request" on an OPEN case.
//
// Sits on the case drawer between the Intake panel and the requests. One more request — its kind,
// the staff's one-line statement, and "What do we already know?" for THIS request — added to the
// case the requester already has. When the request form has already gone out, the API emails the
// requester ("we added to your request"); the notice below says which of the three things happened.
// Hidden for a build-time approval (nothing to add to) and for a closed case.
//
//   cd apps/web && npx tsx --test src/components/rra/AddRequestPanel.test.ts

import { useRef, useState } from 'react';
import { GateFields } from '@/components/rra/GateFields';
import { RequestRowFields } from '@/components/rra/RequestRows';
import { emptyGateDraft, gateDraftToBody, validateGateDraft, type GateDraft } from '@/lib/rraGateDraft';
import {
  addRequestBody,
  addRequestNotice,
  blankRequestRow,
  canAddRequest,
  nextAddAttempt,
  rowProblem,
  type AddAttempt,
  type RequestRow,
} from '@/lib/rraRequests';
import type { AddRraRequestBody, AddRraRequestResponse, RraCaseView } from '@/lib/rraStaffClient';
import type { RraRunFn } from '@/components/rra/AttentionPanel';

export const ADD_REQUEST_HEADING = 'Add a request';
export const ADD_REQUEST_INTRO =
  'Another thing they are asking for? Add it to this case. If the request form has already gone out, we email them to tell us about it.';

export function AddRequestPanel({
  record,
  busy,
  run,
  add,
  initialOpen = false,
}: {
  record: RraCaseView;
  busy: string | null;
  run: RraRunFn;
  /** The API call (the page passes `addRraRequest` bound to this case) — injected so the panel renders without the fetch layer. */
  add: (body: AddRraRequestBody) => Promise<AddRraRequestResponse>;
  /** Test seam: render the panel with the form already open. */
  initialOpen?: boolean;
}): React.JSX.Element | null {
  const [open, setOpen] = useState(initialOpen);
  const [row, setRow] = useState<RequestRow>(() => blankRequestRow());
  const [gate, setGate] = useState<GateDraft>(emptyGateDraft);
  const [touched, setTouched] = useState(false);
  // The idempotency key of the last attempt: pressing Add again on the same request reuses it.
  const attempt = useRef<AddAttempt | null>(null);
  if (!canAddRequest(record)) return null;

  const gateErrors = validateGateDraft(gate);
  const busyHere = busy === 'add-request';
  const reset = (): void => {
    setRow(blankRequestRow());
    setGate(emptyGateDraft());
    setTouched(false);
    setOpen(false);
    attempt.current = null;
  };

  const submit = (): void => {
    const body = gateDraftToBody(gate, row.scope);
    if (rowProblem(row) || !body) {
      setTouched(true);
      return;
    }
    const plain = addRequestBody(row, body);
    attempt.current = nextAddAttempt(attempt.current, plain);
    const clientRequestId = attempt.current.key;
    void run(
      'add-request',
      async () => {
        const res = await add({ ...plain, clientRequestId });
        reset();
        return res;
      },
      (res) => addRequestNotice(res as AddRraRequestResponse | undefined, record.requester.name),
    );
  };

  return (
    <div className="card" data-section="rra-add-request">
      <div style={{ fontWeight: 'var(--weight-medium)', marginBottom: 'var(--space-2)' }}>{ADD_REQUEST_HEADING}</div>
      {!open ? (
        <div className="stack-sm">
          <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>{ADD_REQUEST_INTRO}</div>
          <div>
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              data-size="sm"
              disabled={busy !== null}
              onClick={() => setOpen(true)}
            >
              Add a request…
            </button>
          </div>
        </div>
      ) : (
        <div className="stack">
          <RequestRowFields
            row={row}
            idPrefix="rra-add"
            onChange={(patch) => {
              setRow((r) => ({ ...r, ...patch }));
            }}
            disabled={busy !== null}
            showErrors={touched}
          />
          <GateFields
            value={gate}
            onChange={(next) => {
              setGate(next);
              setTouched(true);
            }}
            letters={record.gateLetterNames ?? []}
            scope={row.scope}
            errors={touched ? gateErrors : []}
            disabled={busy !== null}
          />
          <div className="cluster" style={{ gap: 'var(--space-2)' }}>
            <button
              type="button"
              className="btn"
              data-variant="primary"
              data-size="sm"
              disabled={busy !== null || gateErrors.length > 0}
              onClick={submit}
            >
              {busyHere ? 'Adding…' : 'Add'}
            </button>
            <button type="button" className="btn" data-variant="ghost" data-size="sm" disabled={busy !== null} onClick={reset}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
